import { create } from "zustand";
import { isFeatureEnabled } from "@/services/analytics";

// Check if analytics should be enabled
const shouldUseAnalytics = () => {
  const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
  const isCapacitor = typeof window !== 'undefined' && 'Capacitor' in window;
  return !isDevelopment || isCapacitor;
};

interface FeatureFlagState {
  advancedInventoryTracking: boolean;
  activatePaymentRequest: boolean;
  activateLandingPage: boolean;
  enableSubscription: boolean;
  checkFlag: (flagName: string) => boolean;
  refreshFlags: () => void;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set, get) => ({
  // Default feature flags (will be updated by refreshFlags)
  advancedInventoryTracking: false,
  activatePaymentRequest: false,
  activateLandingPage: false,
  enableSubscription: false,

  // Check any feature flag by name
  checkFlag: (flagName: string): boolean => {
    // For specific named flags we track in the store, check the store value
    if (flagName === "advanced_inventory_tracking") {
      return get().advancedInventoryTracking;
    }
    if (flagName === "activate_payment_request") {
      return get().activatePaymentRequest;
    }
    if (flagName === "activate_landingpage") {
      return get().activateLandingPage;
    }
    if (flagName === "enable_subscription") {
      return get().enableSubscription;
    }

    // For other flags, check directly from PostHog (only if analytics enabled)
    if (shouldUseAnalytics()) {
      return isFeatureEnabled(flagName, false);
    }
    
    // Return default value in development
    return false;
  },

  // Refresh all feature flags from PostHog
  refreshFlags: () => {
    if (!shouldUseAnalytics()) {
      console.log('Feature flags refresh skipped - analytics disabled in development');
      return {
        advancedInventoryEnabled: false,
        paymentRequestEnabled: false,
        landingPageEnabled: false,
        subscriptionEnabled: false,
      };
    }

    const advancedInventoryEnabled = isFeatureEnabled(
      "advanced_inventory_tracking",
      false
    );
    const paymentRequestEnabled = isFeatureEnabled(
      "activate_payment_request",
      false
    );
    const landingPageEnabled = isFeatureEnabled("activate_landingpage", false);
    const subscriptionEnabled = isFeatureEnabled("enable_subscription", false);

    set({
      advancedInventoryTracking: advancedInventoryEnabled,
      activatePaymentRequest: paymentRequestEnabled,
      activateLandingPage: landingPageEnabled,
      enableSubscription: subscriptionEnabled,
    });

    return {
      advancedInventoryEnabled,
      paymentRequestEnabled,
      landingPageEnabled,
      subscriptionEnabled,
    };
  },
}));

// Initialize the feature flags immediately
useFeatureFlagStore.getState().refreshFlags();
